/**
 * Phase 21.4 Batch 3E — Contest status derivation helpers.
 *
 * Two axes:
 *   1. The raw `status` field on the contest doc (draft / published /
 *      open / judging / closed / archived).
 *   2. The current wall-clock time vs the four configured timestamps:
 *        starts_at              → contest officially begins
 *        submission_deadline    → entries must be in by this moment
 *        ends_at                → the contest itself ends
 *        results_at             → when winners are expected/published
 *
 * Display states (Batch 3E — surfaces MUST use these):
 *   - "coming_soon"        → before starts_at
 *   - "open"               → between starts_at and submission_deadline
 *   - "submissions_closed" → past submission_deadline, before judging
 *   - "judging"            → status=judging (or between deadline and results_at)
 *   - "results_published"  → status=closed/archived AND winners committed
 *   - "closed"             → status=closed/archived AND no winners
 *   - "draft"              → status=draft (admin-only surfaces)
 */

export const DISPLAY_STATUS = {
  coming_soon: {
    key: "coming_soon",
    label: "Coming Soon",
    tone: "bg-white/10 text-white/80 border-white/20",
    dot: "bg-white/40",
    countdown_label: "Contest starts in",
  },
  open: {
    key: "open",
    label: "Open",
    tone: "bg-emerald-500/15 text-emerald-200 border-emerald-500/40",
    dot: "bg-emerald-400",
    countdown_label: "Submissions close in",
  },
  submissions_closed: {
    key: "submissions_closed",
    label: "Submissions Closed",
    tone: "bg-amber-500/15 text-amber-200 border-amber-500/40",
    dot: "bg-amber-400",
    countdown_label: null,
  },
  judging: {
    key: "judging",
    label: "Judging in Progress",
    tone: "bg-amber-500/15 text-amber-200 border-amber-500/40",
    dot: "bg-amber-400",
    countdown_label: "Results in",
  },
  results_published: {
    key: "results_published",
    label: "Results Published",
    tone: "bg-purple-500/20 text-purple-200 border-purple-500/40",
    dot: "bg-purple-400",
    countdown_label: null,
  },
  closed: {
    key: "closed",
    label: "Contest Closed",
    tone: "bg-purple-500/20 text-purple-200 border-purple-500/40",
    dot: "bg-purple-400",
    countdown_label: null,
  },
  draft: {
    key: "draft",
    label: "Draft",
    tone: "bg-white/5 text-gray-400 border-white/10",
    dot: "bg-gray-400",
    countdown_label: null,
  },
};

const parseIso = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Given a contest document, return one of the DISPLAY_STATUS entries.
 * Never throws — bad data falls back to "coming_soon".
 */
export function deriveContestDisplayStatus(contest, now = new Date()) {
  if (!contest || typeof contest !== "object") return DISPLAY_STATUS.coming_soon;
  const raw = contest.status;
  if (raw === "draft") return DISPLAY_STATUS.draft;

  const startsAt = parseIso(contest.starts_at);
  const deadline = parseIso(contest.submission_deadline);
  const winnersCommitted = Array.isArray(contest.winners) && contest.winners.length > 0;

  if (raw === "closed" || raw === "archived") {
    return winnersCommitted ? DISPLAY_STATUS.results_published : DISPLAY_STATUS.closed;
  }
  if (raw === "judging") return DISPLAY_STATUS.judging;

  // published / open path — refine using timestamps.
  if (startsAt && now < startsAt) return DISPLAY_STATUS.coming_soon;
  if (deadline && now >= deadline) {
    // Between deadline & judging flip — surface "Submissions Closed"
    // so artists don't see a misleading "Ends in" countdown.
    return DISPLAY_STATUS.submissions_closed;
  }
  return DISPLAY_STATUS.open;
}

/** True if artists should be able to enter right now. */
export function canEnterContest(contest, now = new Date()) {
  if (!contest) return false;
  const raw = contest.status;
  if (raw !== "published" && raw !== "open") return false;
  const startsAt = parseIso(contest.starts_at);
  if (startsAt && now < startsAt) return false;
  const deadline = parseIso(contest.submission_deadline);
  if (deadline && now >= deadline) return false;
  return true;
}

/**
 * Countdown target + label. Returns {target, label} or null when
 * there's nothing meaningful to show.
 *  - coming_soon  → starts_at
 *  - open         → submission_deadline
 *  - judging      → results_at (if set)
 *  - others       → null (no countdown to display)
 */
export function contestCountdownTarget(contest, now = new Date()) {
  if (!contest) return null;
  const display = deriveContestDisplayStatus(contest, now);
  if (display.key === "coming_soon" && contest.starts_at) {
    return { target: contest.starts_at, label: display.countdown_label };
  }
  if (display.key === "open" && contest.submission_deadline) {
    return { target: contest.submission_deadline, label: display.countdown_label };
  }
  if (display.key === "judging" && contest.results_at) {
    return { target: contest.results_at, label: display.countdown_label };
  }
  return null;
}

/** Plain-English label for an ENTRY status. */
export const ENTRY_STATUS = {
  pending: { label: "Pending Review", tone: "bg-white/10 text-white/80 border-white/15" },
  approved: { label: "Approved", tone: "bg-cyan-500/15 text-cyan-200 border-cyan-500/40" },
  rejected: { label: "Not Selected", tone: "bg-rose-500/15 text-rose-200 border-rose-500/40" },
  withdrawn: { label: "Withdrawn", tone: "bg-white/5 text-gray-400 border-white/10" },
  winner: { label: "Winner", tone: "bg-amber-500/20 text-amber-100 border-amber-500/50" },
  runner_up: { label: "Runner-Up", tone: "bg-amber-500/10 text-amber-200 border-amber-500/30" },
};

export function entryDisplayStatus(entry) {
  if (!entry) return ENTRY_STATUS.pending;
  const s = entry.status;
  if (s === "winner") {
    if (entry.position === 1) return { ...ENTRY_STATUS.winner, label: "🥇 1st Place" };
    if (entry.position === 2) return { ...ENTRY_STATUS.winner, label: "🥈 2nd Place" };
    if (entry.position === 3) return { ...ENTRY_STATUS.winner, label: "🥉 3rd Place" };
    return ENTRY_STATUS.winner;
  }
  return ENTRY_STATUS[s] || ENTRY_STATUS.pending;
}

export function formatFullDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return d.toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    });
  } catch {
    return d.toISOString();
  }
}
